import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HomeIcon from "@mui/icons-material/Home";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

type Dates = {
  created?: string | null;
  enRoute?: string | null;
  arrived?: string | null;
  delivered?: string | null;
};

const ORDER_LABELS = {
  created: "Created",
  enRoute: "En\u00A0Route",
  arrived: "Arrived",
  delivered: "Delivered",
} as const;

const ORDER_ICONS = {
  created: <ShoppingCartIcon />,
  enRoute: <LocalShippingIcon />,
  arrived: <HomeIcon />,
  delivered: <EmojiPeopleIcon />,
} as const;

const OrderStatusTimeline = ({ dates }: { dates: Dates }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const activeColor = theme.palette.info.light;
  const keys: (keyof Dates)[] = ["created", "enRoute", "arrived", "delivered"];
  const completedIndex = keys.reduce((acc, k, i) => (dates[k] ? i : acc), -1);
  const progressPercentage =
    keys.length > 1
      ? (Math.max(Math.min(completedIndex + 1, keys.length - 1), 0) /
          (keys.length - 1)) *
        100
      : 0;

  const formatDate = (date?: string | null) => {
    if (!date) return null;

    const utcDateString = date + "Z";

    return dayjs.utc(utcDateString).local().format("MM/DD/YYYY hh:mm:ss A");
  };

  const Step = ({
    label,
    date,
    icon,
    completed,
  }: {
    label: string;
    date?: string | null;
    icon: React.ReactNode;
    completed?: boolean;
  }) => (
    <Stack direction="column" alignItems="center">
      <Box
        sx={{
          position: "relative",
          height: { xs: 36, sm: 10 },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            "& svg": {
              fontSize: { xs: 24, sm: 28, md: 32 },
              color: "text.primary",
            },
          }}
        >
          {icon}
        </Box>

        {completed && (
          <CheckCircleIcon
            sx={{
              position: "absolute",
              bottom: 22,
              left: 22,
              fontSize: { xs: 18, sm: 20 },
              color: activeColor,
              backgroundColor: theme.palette.background.paper,
              borderRadius: "50%",
            }}
          />
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
        {label}
      </Typography>

      <Typography
        variant="caption"
        color={date ? "#1976D2" : "text.secondary"}
        sx={{
          whiteSpace: "nowrap",
          fontSize: "14px",
          display: { xs: "none", md: "block" },
        }}
      >
        {formatDate(date) || "Pending"}
      </Typography>
    </Stack>
  );

  return (
    <Box
      sx={{
        pb: 1,
        position: "relative",
        overflowX: { xs: "auto", sm: "visible" },
        width: isMobile ? "90%" : "50%",
        mx: "auto",
      }}
    >
      <Box sx={{ position: "relative", width: isMobile ? "100%" : "100%" }}>
        <Box
          sx={{
            height: 3,
            borderRadius: 2,
            mb: { xs: 1, sm: 5 },
            background: `linear-gradient(
              to right,
              ${activeColor} 0%,
              ${activeColor} ${progressPercentage}%,
              ${theme.palette.divider} ${progressPercentage}%,
              ${theme.palette.divider} 100%
            )`,
          }}
        />
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={isMobile ? 2 : 0}
          sx={{
            position: "relative",
            scrollSnapType: isMobile ? "x mandatory" : "none",
          }}
        >
          {keys.map((key, index) => (
            <Box
              key={key}
              sx={{ scrollSnapAlign: isMobile ? "start" : "none" }}
            >
              <Step
                label={ORDER_LABELS[key]}
                date={dates[key]}
                icon={ORDER_ICONS[key]}
                completed={index <= completedIndex && !!dates[key]}
              />
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default OrderStatusTimeline;
